import { forwardRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

/**
 * Content interface for MapMarker label
 */
export interface MapMarkerContent {
  /**
   * The number or symbol to display in the marker (any positive number or "#")
   */
  label: string | number;
}

/**
 * Props interface for MapMarker component
 */
export interface MapMarkerProps {
  /**
   * Content object containing the marker label
   */
  content: MapMarkerContent;

  /**
   * Whether the location has a physical address
   * true: Primary Brand Color background (#0A4D8F)
   * false: System State Success background (#117E1A)
   */
  hasAddress: boolean;

  /**
   * Whether the marker is currently selected
   * @default false
   */
  isSelected?: boolean;

  /**
   * Click handler for marker interaction
   */
  onClick?: () => void;

  /**
   * Additional CSS classes for styling
   */
  className?: string;

  /**
   * Accessible label for screen readers describing the location
   */
  ariaLabel?: string;
}

/**
 * MapMarker component for displaying numbered location markers on maps
 * with visual differentiation for locations with and without addresses.
 *
 * @example
 * ```tsx
 * import { MapMarker } from '@/components';
 *
 * <MapMarker
 *   content={{ label: 1 }}
 *   hasAddress={true}
 *   isSelected={false}
 *   onClick={() => console.log('Marker clicked')}
 *   ariaLabel="Location 1: Community Food Bank"
 * />
 * ```
 */
export const MapMarker = forwardRef<HTMLButtonElement, MapMarkerProps>(
  (
    { content, hasAddress, isSelected = false, onClick, className, ariaLabel },
    ref
  ) => {
    const handleClick = useCallback(() => {
      onClick?.();
    }, [onClick]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      },
      [onClick]
    );

    // Validate label - should be a positive number or "#"
    const isValidLabel =
      content.label === '#' ||
      (typeof content.label === 'number' && content.label >= 1) ||
      (typeof content.label === 'string' && /^[1-9]\d*$/.test(content.label));

    if (!isValidLabel) {
      return null;
    }

    // Determine font size based on number of digits
    // 1-99: normal (text-xl, 20px)
    // 100-999: smaller (text-sm, 14px)
    // 1000+: smallest (text-xs, 12px)
    const labelNum = typeof content.label === 'number'
      ? content.label
      : parseInt(content.label, 10);

    const getFontSizeClass = () => {
      if (isNaN(labelNum) || labelNum < 100) return 'text-xl leading-7';
      if (labelNum < 1000) return 'text-sm leading-5';
      return 'text-xs leading-4';
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel || `Location ${content.label}`}
        aria-pressed={isSelected}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'w-9 h-9 rounded-full p-1 gap-2.5',
          // Typography - font size is dynamic based on label length
          'font-semibold text-white',
          getFontSizeClass(),
          "font-['Source_Sans_Pro']",
          // Background color based on hasAddress with hover/active states
          hasAddress
            ? 'bg-[var(--color-marker-primary)] hover:bg-[var(--color-marker-primary-hover)] active:bg-[var(--color-marker-primary-hover)]'
            : 'bg-[var(--color-marker-success)] hover:bg-[var(--color-marker-success-hover)] active:bg-[var(--color-marker-success-hover)]',
          // Selected state border
          isSelected && 'ring-[3px] ring-[var(--color-marker-selected)]',
          // Interactive states
          'cursor-pointer',
          // Focus state
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
          // Transitions with reduced motion support
          'transition-colors duration-150 ease-out',
          'motion-reduce:transition-none',
          className
        )}
      >
        {content.label}
      </button>
    );
  }
);

MapMarker.displayName = 'MapMarker';
